import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError, timer, TimeoutError } from 'rxjs';
import { catchError, switchMap, takeWhile, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AccountBackupRequest, AccountBackupStatus, BackupFormat } from '../model';

/**
 * Data/API layer for the "back up all my chats" feature.
 *
 * The backend generates a backup of all of the authenticated broker's chats
 * (JSON / CSV) asynchronously: POST to start a job, then poll the
 * status endpoint until the job is DONE (a presigned download URL appears) or
 * FAILED. The backup is scoped to the user by their token — no id parameters.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatBackupService {
  private static readonly POLL_MS = 2500;
  private static readonly SAFETY_TIMEOUT_MS = 5 * 60 * 1000; // ~5 minutes

  private readonly baseUrl = `${environment.apiEndpoint}/chat/data/account-backup`;

  constructor(private http: HttpClient) {}

  /**
   * Start a backup job. If a backup is already running for this user the
   * backend returns the existing in-progress job (same jobId) — treat it as a
   * normal start and poll the returned jobId.
   */
  public startBackup(format: BackupFormat, from?: string | null, to?: string | null): Observable<AccountBackupStatus> {
    const body: AccountBackupRequest = { format, from: from ?? null, to: to ?? null };
    return this.http.post<AccountBackupStatus>(this.baseUrl, body);
  }

  /**
   * Poll the status of a backup job once. Emits a 404 error if the job id is
   * unknown or belongs to another user.
   */
  public getStatus(jobId: string): Observable<AccountBackupStatus> {
    return this.http.get<AccountBackupStatus>(`${this.baseUrl}/${jobId}`);
  }

  /**
   * Start a backup and poll until the job reaches a terminal state.
   *
   * Emits every polled status (each IN_PROGRESS plus the final DONE) so the UI
   * can show progress, and completes once the job is DONE. A FAILED job or an
   * overall timeout (~5 min still IN_PROGRESS) is surfaced as an error so the
   * design component can show it and avoid attempting a download.
   *
   * Fully observable-based and unsubscribe-safe: unsubscribing stops the poll
   * timer, so there are no leaked intervals.
   */
  public runBackup(format: BackupFormat, from?: string | null, to?: string | null): Observable<AccountBackupStatus> {
    return this.startBackup(format, from, to).pipe(
      switchMap((started) =>
        timer(0, ChatBackupService.POLL_MS).pipe(
          switchMap(() => this.getStatus(started.jobId)),
          switchMap((status) =>
            status.status === 'FAILED' ? throwError(() => new Error('Chat backup failed')) : of(status),
          ),
          // inclusive: also emit the terminal (DONE) status, then complete
          takeWhile((status) => status.status === 'IN_PROGRESS', true),
          timeout(ChatBackupService.SAFETY_TIMEOUT_MS),
        ),
      ),
      catchError((err: unknown) => {
        if (err instanceof TimeoutError) {
          return throwError(() => new Error('Chat backup timed out'));
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Trigger the browser download for a finished backup. The downloadUrl is a
   * cross-origin presigned S3 link (valid ~5 min) with content-type/filename
   * set server-side (.json / .csv), so we just navigate to it. Call
   * immediately on DONE.
   */
  public triggerDownload(status: AccountBackupStatus): void {
    if (status?.status !== 'DONE' || !status.downloadUrl) {
      return;
    }
    const link = document.createElement('a');
    link.href = status.downloadUrl;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
